/**
 * <TI /> — canonical brand wordmark for the ToxIntel name.
 *
 * Renders as `Tox<em>Intel</em>` welded into a single visual unit, with
 *   - whiteSpace: nowrap        → never breaks across lines
 *   - letterSpacing: normal     → cancels parent `tracking-wider` / uppercase
 *                                 tracking that would otherwise split "Tox"
 *                                 and italic "Intel" visually on mobile CTAs.
 *   - <em> margin-left: -0.04em → optical kerning between roman "Tox" and
 *                                 italic "Intel" so the wordmark reads as
 *                                 one premium brand word.
 *
 * Use bare "ToxIntel" only for places where italicization is meaningless
 * or harmful: aria-labels, file names, image alt text, JSON-LD, OG metadata,
 * back-end strings, OS-level title bars, PDF generators.
 *
 * Variants:
 *   <TI />                    → Tox<em>Intel</em>  (italic Intel inherits color)
 *   <TI accent />             → italic Intel rendered in coral (#FF4E6A)
 *   <TI color="#E63946" />    → italic Intel in arbitrary hex
 *   <TI as="span" />          → wrap explicitly in <span> (default is span)
 */
import React from 'react';

const CORAL = '#FF4E6A';

// Tight kerning so "Tox" + italic "Intel" appear welded as a single brand word.
// Critical inside CTAs that apply `tracking-wider` + `uppercase`.
const WRAP_STYLE = {
  whiteSpace: 'nowrap',
  letterSpacing: 'normal',
};

const EM_BASE = {
  fontStyle: 'italic',
  fontWeight: 'inherit',
  marginLeft: '-0.04em',
  letterSpacing: 'normal',
};

export default function TI({ accent = false, color, className, as: As = 'span', style, ...rest }) {
  const wrapStyle = { ...WRAP_STYLE, ...(style || {}) };
  const emColor = color || (accent ? CORAL : undefined);
  const emStyle = emColor ? { ...EM_BASE, color: emColor } : EM_BASE;
  return (
    <As className={className} style={wrapStyle} {...rest}>
      Tox<em style={emStyle}>Intel</em>
    </As>
  );
}
